import { useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import SearchBar from './components/SearchBar';
import { checkDomain, DomainInfo, AlternativeSuggestion } from './services/api';
import ErrorBoundary from './components/ErrorBoundary';
import WhyItMattersSection from './components/WhyItMattersSection';
import { Suspense, lazy } from 'react';
import { MetaTags } from './components/seo/MetaTags';
import { generateWebsiteSchema, generateWebPageSchema } from './utils/schema';
import { trackPageView, trackDomainSearch, trackAlternativesViewed } from './utils/analytics';

const DomainResultsDisplay = lazy(() => import('./components/DomainResultsDisplay'));
const TechNewsSection = lazy(() => import('./components/TechNewsSection'));

const App = () => {
  const location = useLocation();
  const [searchResult, setSearchResult] = useState<DomainInfo | null>(null);
  const [alternatives, setAlternatives] = useState<AlternativeSuggestion[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track page views when location changes
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location]);

  // Generate structured data for the homepage
  const structuredData = useMemo(
    () => ({
      website: generateWebsiteSchema({
        name: 'DomainRadar',
        url: 'https://domainradar.lt',
        description: 'Check domain availability and compare prices across multiple registrars.',
        inLanguage: 'en-US',
        logo: 'https://domainradar.lt/logo.png',
        sameAs: ['https://twitter.com/domainradar', 'https://facebook.com/domainradar'],
      }),
      webpage: generateWebPageSchema({
        name: 'DomainRadar - Domain Availability Checker',
        url: `https://domainradar.lt${location.pathname}`,
        description:
          'Check domain availability and compare prices across multiple registrars. Find the best domain for your website with DomainRadar.',
        headline: 'Domain Availability Checker',
        inLanguage: 'en-US',
        datePublished: '2025-01-01T00:00:00+03:00',
        author: {
          '@type': 'Organization',
          name: 'DomainRadar',
          url: 'https://domainradar.lt',
        },
        publisher: {
          '@type': 'Organization',
          name: 'DomainRadar',
          logo: {
            '@type': 'ImageObject',
            url: 'https://domainradar.lt/logo.png',
            width: 600,
            height: 60,
          },
        },
        image: 'https://domainradar.lt/og-image.png',
      }),
    }),
    [location.pathname]
  );

  const handleSearch = async (domain: string) => {
    setIsLoading(true);
    setError(null);
    setSearchResult(null);
    setAlternatives(null);

    try {
      const result = await checkDomain(domain);
      setSearchResult(result.domain);
      setAlternatives(result.alternatives || null);

      // Track domain search
      trackDomainSearch(domain, result.domain.available);

      // Track alternatives viewed if any
      if (result.alternatives && result.alternatives.length > 0) {
        const availableAlternatives = result.alternatives.filter(alt => alt.available);
        trackAlternativesViewed(domain, availableAlternatives.length);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Wrapper to make onSearch not async
  const handleSearchWrapper = (domain: string) => {
    void handleSearch(domain);
  };

  return (
    <>
      <MetaTags
        title="DomainRadar - Check Domain Availability & Prices"
        description="Check domain availability and compare prices across multiple registrars. Find the best domain for your website with DomainRadar."
        structuredData={structuredData}
      />
      <ErrorBoundary>
        <div className="min-h-screen bg-gray-50 flex flex-col">
          <main className="flex-1 flex flex-col items-center justify-start px-2 sm:px-0 pt-8 pb-8">
            <div className="w-full max-w-2xl mx-auto">
              <h1 className="text-3xl sm:text-4xl font-extrabold text-center text-blue-800 mb-2 flex justify-center items-center gap-2">
                <svg
                  className="w-9 h-9 animate-spin-slow text-blue-500"
                  viewBox="0 0 36 36"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ verticalAlign: 'middle' }}
                >
                  {/* Outer circles */}
                  <circle
                    cx="18"
                    cy="18"
                    r="15"
                    stroke="currentColor"
                    strokeWidth="2"
                    opacity="0.18"
                  />
                  <circle
                    cx="18"
                    cy="18"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    opacity="0.25"
                  />
                  <circle
                    cx="18"
                    cy="18"
                    r="5.5"
                    stroke="currentColor"
                    strokeWidth="1"
                    opacity="0.33"
                  />
                  {/* Radial lines */}
                  <line
                    x1="18"
                    y1="18"
                    x2="33"
                    y2="18"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    opacity="0.7"
                  />
                  <line
                    x1="18"
                    y1="18"
                    x2="28.7"
                    y2="7.5"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    opacity="0.5"
                  />
                  <line
                    x1="18"
                    y1="18"
                    x2="18"
                    y2="3"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    opacity="0.5"
                  />
                  {/* Sektorius (arc) – skenuojantis plotas */}
                  <path d="M18 18 L18 4 A14 14 0 0 1 32 18 Z" fill="currentColor" opacity="0.15" />
                  {/* Center point */}
                  <circle cx="18" cy="18" r="2" fill="currentColor" />
                </svg>
                <span role="img" aria-label="domain">
                  Domain finder
                </span>
              </h1>
              <p className="text-center text-gray-500 mb-6">
                Powered by{' '}
                <a
                  href="https://itwithai.lt/index_en.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-2 py-0.5 rounded-md transition-all duration-200 inline-flex items-center group"
                >
                  IT with AI
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 ml-1 text-blue-500 group-hover:translate-x-0.5 transition-transform"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </a>
              </p>
              <SearchBar onSearch={handleSearchWrapper} isLoading={isLoading} />
              <Suspense
                fallback={
                  <div className="my-8 w-full max-w-2xl mx-auto">
                    <div className="skeleton h-32 w-full rounded mb-4"></div>
                  </div>
                }
              >
                <DomainResultsDisplay
                  searchResult={searchResult}
                  alternatives={alternatives}
                  isLoading={isLoading}
                  error={error}
                />
              </Suspense>
              <div className="mt-10">
                <WhyItMattersSection />
              </div>
              <Suspense
                fallback={
                  <div className="my-8 w-full max-w-2xl mx-auto">
                    <div className="skeleton h-16 w-2/3 rounded mb-2"></div>
                    <div className="skeleton h-8 w-1/2 rounded"></div>
                  </div>
                }
              >
                <TechNewsSection />
              </Suspense>
            </div>
          </main>
          <footer className="text-center text-gray-400 text-xs py-4">
            &copy; {new Date().getFullYear()}
            <span className="inline-flex items-center gap-1">
              Domain finder
              <svg
                className="ml-1 w-4 h-4 animate-spin-slow text-blue-500"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ verticalAlign: 'middle' }}
              >
                <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="2" opacity="0.3" />
                <path
                  d="M10 2a8 8 0 0 1 8 8"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            . All rights reserved.
          </footer>

          <style>
            {`
            @keyframes spin-slow {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            .animate-spin-slow {
              animation: spin-slow 2s linear infinite;
            }
          `}
          </style>
        </div>
      </ErrorBoundary>
    </>
  );
};

export default App;
